import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { LevelBadge } from "@/components/LevelBadge";
import LiveLeaderboard from "@/components/LiveLeaderboard";
import LiveTop5Board from "@/components/LiveTop5Board";
import useLiveLeaderboard from "@/hooks/useLiveLeaderboard";
import ScoringLegend from "@/components/ScoringLegend";
import HighlightsButton from "@/components/HighlightsButton";
import { AiCoachPanel } from "@/components/AiCoachPanel";
import ChallengeChat from "@/components/ChallengeChat";
import { RecapShare } from "@/components/RecapShare";
import TermsModal from "@/components/TermsModal";
import { toast } from "sonner";
import { Timer, ArrowRight, X, Zap, Lock, Flame, Sparkles, Loader2, Users, Copy } from "lucide-react";

const NoPrizePanel = () => (
  <div className="border border-white/10 bg-[#141414] p-5 text-center" data-testid="no-prize-panel">
    <div className="text-white/40 text-xs uppercase tracking-widest mb-1">Playing for</div>
    <div className="font-display text-2xl">BRAGGING RIGHTS</div>
    <div className="text-white/50 text-xs mt-1">
      Free to enter · no cash prize · top five gets the glory and the XP
    </div>
  </div>
);


export default function Contest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [contest, setContest] = useState(null);
  const [picks, setPicks] = useState([]);
  const [tbPlayer, setTbPlayer] = useState("");
  const [tbValue, setTbValue] = useState("");
  const [tbKey, setTbKey] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [notes, setNotes] = useState(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [friendsInfo, setFriendsInfo] = useState(null);

  useEffect(() => {
    if (contest?.mode !== "friends") return;
    api.get(`/friends/challenges/${id}`).then(({ data }) => setFriendsInfo(data)).catch(() => {});
  }, [contest?.mode, id]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/contests/${id}`);
        setContest(data);
        setTbKey(data.tiebreakers[0].key);
      } catch {
        toast.error("Contest not found");
        navigate("/lobby");
      }
    })();
    // Refresh contest every 15s to detect lock/settlement transitions
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/contests/${id}`);
        setContest(data);
      } catch {
        /* ignore */
      }
    }, 15000);
    return () => clearInterval(t);
  }, [id, navigate]);

  const playerMap = useMemo(() => {
    if (!contest) return {};
    return Object.fromEntries(contest.players.map((p) => [p.player_id, p]));
  }, [contest]);

  const applyAiPicks = (ids) => {
    setPicks(ids.slice(0, 5));
    toast.success("AI ranking loaded — reorder or swap anyone before you enter");
  };

  const loadNotes = async () => {
    setNotesLoading(true);
    try {
      const { data } = await api.get(`/ai/insights/${id}`);
      setNotes(data.insights || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scouting notes unavailable");
    } finally {
      setNotesLoading(false);
    }
  };

  const isOpen = contest?.status === "open";
  const isLocked = contest?.status === "locked";
  const isSettled = contest?.status === "settled";
  const isPractice = (contest?.entry_fee ?? 0) <= 0;

  // Locked/settled pages poll once here and share the payload with both boards
  const { data: board } = useLiveLeaderboard(id, { enabled: !!contest && !isOpen });
  const myPicks = useMemo(() => {
    const mine = (board?.entries || []).find((e) => e.user_id === user?.user_id);
    return mine?.picks || [];
  }, [board, user]);

  const togglePick = (pid) => {
    if (!isOpen) return;
    if (picks.includes(pid)) {
      setPicks(picks.filter((x) => x !== pid));
      if (tbPlayer === pid) setTbPlayer("");
    } else if (picks.length < 5) {
      setPicks([...picks, pid]);
    } else {
      toast.error("You can only pick 5 players");
    }
  };

  const movePick = (idx, dir) => {
    const next = [...picks];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setPicks(next);
  };

  const submit = async () => {
    if (picks.length !== 5) return toast.error("Pick 5 players in ranked order");
    if (!tbPlayer) return toast.error("Choose a tiebreaker player");
    if (tbValue === "" || Number.isNaN(Number(tbValue)))
      return toast.error("Enter a numeric tiebreaker value");
    if (!isPractice && user && (!user.terms_accepted_at || !user.date_of_birth)) {
      setShowTerms(true);
      return;
    }

    setSubmitting(true);
    try {
      // One money path only: deposit → wallet → entry. Practice contests are free.
      await api.post("/contests/enter-from-wallet", {
        contest_id: id,
        picks,
        tiebreaker_player_id: tbPlayer,
        tiebreaker_value: Number(tbValue),
      });
      toast.success(isPractice ? "You're in! Free practice entry submitted." : "You're in! Entry paid from wallet.");
      navigate("/entries");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Entry failed";
      const lower = msg.toLowerCase();
      if (lower.includes("terms") || lower.includes("date of birth")) {
        setShowTerms(true);
      } else if (lower.includes("insufficient")) {
        toast.error(msg, { action: { label: "Add funds", onClick: () => navigate("/wallet") } });
      } else {
        toast.error(msg);
      }
      setSubmitting(false);
    }
  };

  if (!contest) {
    return (
      <Layout>
        <div className="text-white/40 text-center py-20 font-display tracking-widest text-2xl animate-pulse">
          LOADING...
        </div>
      </Layout>
    );
  }

  const activeTb = contest.tiebreakers.find((t) => t.key === tbKey) || contest.tiebreakers[0];
  const statusBadge = isSettled ? (
    <span className="text-xs uppercase tracking-widest bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 px-2 py-1 rounded-sm">
      Settled
    </span>
  ) : isLocked ? (
    <span className="text-xs uppercase tracking-widest bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/40 px-2 py-1 rounded-sm inline-flex items-center gap-1">
      <Lock size={11} /> Locked
    </span>
  ) : (
    <span className="text-xs uppercase tracking-widest bg-[#00FF66]/20 text-[#00FF66] border border-[#00FF66]/40 px-2 py-1 rounded-sm inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 bg-[#00FF66] rounded-full animate-pulse" /> Live
    </span>
  );

  return (
    <Layout>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate("/lobby")}
            className="text-white/40 hover:text-white text-xs uppercase tracking-widest mb-2"
            data-testid="back-to-lobby"
          >
            ← Back to Lobby
          </button>
          <div className="flex items-center gap-2 mb-1">
            <div className="text-[#00FF66] text-xs uppercase tracking-widest">
              {contest.position} · Game Day {contest.game_day}
            </div>
            {statusBadge}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-6xl tracking-tight">{contest.title.toUpperCase()}</h1>
          {isPractice && (
            <div
              className="mt-2 inline-flex items-start gap-2 border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 text-[#FF9F0A] text-[10px] md:text-xs uppercase tracking-widest px-3 py-1.5"
              data-testid="practice-banner"
            >
              <Zap size={12} className="flex-shrink-0 mt-0.5" /> Practice · free entry · simulated results · no payout or XP
            </div>
          )}
          {friendsInfo?.invite_code && (
            <div className="mt-2 flex items-center gap-2 flex-wrap" data-testid="invite-code-chip">
              <div className="inline-flex items-center gap-2 border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] px-3 py-1.5">
                <Users size={12} />
                <span className="text-[10px] uppercase tracking-widest">Invite code</span>
                <span className="font-display text-lg tracking-[0.2em]">{friendsInfo.invite_code}</span>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`${window.location.origin}/api/invite/${friendsInfo.invite_code}`);
                  toast.success("Invite link copied — it unfurls with a preview card");
                }}
                data-testid="copy-invite-link-btn"
                className="border border-white/20 hover:border-[#FFD700] text-white/70 hover:text-white text-[10px] uppercase tracking-widest font-bold px-2.5 py-2 inline-flex items-center gap-1.5"
              >
                <Copy size={11} /> Copy invite link
              </button>
              <span className="text-white/40 text-[10px] uppercase tracking-widest">
                {friendsInfo.members} invited · free to enter · no cash prize
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 text-right">
          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest">Entry</div>
            <div className="font-display text-2xl">FREE</div>
          </div>
          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest">Prize</div>
            <div className="font-display text-2xl text-white/40">NONE</div>
          </div>
          <div>
            <div className="text-white/40 text-xs uppercase tracking-widest">Field</div>
            <div className="font-display text-2xl">
              {contest.current_entries}
              <span className="text-white/40 text-base">
                /{contest.max_entries ? (contest.max_entries >= 1000 ? `${contest.max_entries / 1000}K` : contest.max_entries) : "—"}
              </span>
            </div>
          </div>
          <div>
            <LevelBadge level={contest.tier_level} />
          </div>
        </div>
      </div>

      {isOpen ? (
        <div className="grid lg:grid-cols-3 gap-4 pb-52 md:pb-40">
          {/* Player pool */}
          <div className="lg:col-span-2 min-w-0 border border-white/10 bg-[#141414]">
            <div className="border-b border-white/10 p-4 flex items-center justify-between">
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Available</div>
                <div className="font-display text-xl">{contest.players.length} PLAYERS</div>
              </div>
              <div className="flex items-center gap-3 flex-wrap justify-end">
                {isOpen && !notes && (
                  <button
                    onClick={loadNotes}
                    disabled={notesLoading}
                    data-testid="ai-notes-btn"
                    className="border border-[#7C3AED]/50 hover:bg-[#7C3AED]/15 disabled:opacity-50 text-[#C4B5FD] text-[10px] md:text-xs uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm inline-flex items-center gap-1.5"
                  >
                    {notesLoading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                    {notesLoading ? "Scouting..." : "AI notes"}
                  </button>
                )}
                <div className="text-xs text-white/40 uppercase tracking-widest inline-flex items-center gap-1.5">
                  <Timer size={12} /> Locks{" "}
                  {new Date(contest.lock_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
              </div>
            </div>
            {/* Scoring legend */}
            <ScoringLegend position={contest.position} />
            <div className="divide-y divide-white/10" data-testid="player-list">
              {contest.players.map((p) => {
                const idx = picks.indexOf(p.player_id);
                const isPicked = idx >= 0;
                return (
                  <div
                    key={p.player_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => togglePick(p.player_id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        togglePick(p.player_id);
                      }
                    }}
                    data-testid={`player-row-${p.player_id}`}
                    className={`w-full flex items-center gap-3 md:gap-4 p-3 md:p-4 text-left cursor-pointer transition-colors ${
                      isPicked ? "bg-[#00FF66]/5" : "hover:bg-white/5"
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-sm border flex items-center justify-center font-display text-lg flex-shrink-0 ${
                        isPicked
                          ? "bg-[#00FF66] text-black border-[#00FF66]"
                          : "border-white/20 text-white/30"
                      }`}
                    >
                      {isPicked ? idx + 1 : ""}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate flex items-center gap-2">
                        {p.name}
                        {p.is_hot && (
                          <span
                            className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm"
                            style={{
                              background: "linear-gradient(135deg,#FF3B30 0%,#FF9F0A 100%)",
                              color: "#000",
                            }}
                            title={`Rising: ${p.recent_games?.join(' → ')}`}
                            data-testid={`hot-streak-${p.player_id}`}
                          >
                            <Flame size={11} /> HOT
                          </span>
                        )}
                        <HighlightsButton player={p} />
                      </div>
                      {notes?.[p.player_id] && (
                        <div
                          className="text-[#C4B5FD] text-xs leading-snug mt-0.5 flex items-start gap-1.5"
                          data-testid={`ai-note-${p.player_id}`}
                        >
                          <Sparkles size={11} className="flex-shrink-0 mt-0.5" />
                          <span>{notes[p.player_id]}</span>
                        </div>
                      )}
                      <div className="text-white/40 text-xs uppercase tracking-widest">
                        {p.team} · {p.position}
                        {p.recent_games && (
                          <span className="ml-2 normal-case tracking-normal text-white/30">
                            L3: {p.recent_games.join(' → ')}
                          </span>
                        )}
                      </div>
                      {p.proj_breakdown && p.proj_breakdown.length > 0 && (
                        <div className="text-white/50 text-xs mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                          {p.proj_breakdown.slice(0, 4).map((b) => (
                            <span key={b.stat}>
                              <span className="text-white/40">{b.stat.replace(/_/g, " ")}</span>{" "}
                              <span className="text-white">{b.value}</span>
                              <span
                                className="ml-1 font-display"
                                style={{ color: b.points < 0 ? "#FF3B30" : "#00FF66" }}
                              >
                                {b.points > 0 ? "+" : ""}
                                {b.points.toFixed(1)}
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-white/40 text-xs uppercase tracking-widest">Fantasy</div>
                      <div className="font-display text-2xl text-[#00FF66]">{p.proj_pts.toFixed(1)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Your picks + tiebreaker — first on phones so taps land where you can see them */}
          <div className="space-y-4 min-w-0 order-first lg:order-none">
            {isSettled && <RecapShare contestId={id} />}
            {isOpen && (
              <AiCoachPanel
                contestId={id}
                players={contest.players}
                onApply={applyAiPicks}
                disabled={!isOpen}
              />
            )}
            <div className="border border-white/10 bg-[#141414] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-white/40 text-xs uppercase tracking-widest">Your Ranking</div>
                <div className="font-display text-lg" data-testid="picks-count">
                  {picks.length}/5
                </div>
              </div>
              {picks.length === 0 ? (
                <div className="text-white/30 text-sm py-6 text-center border border-dashed border-white/10">
                  Tap a player to add. Order = rank.
                </div>
              ) : (
                <div className="space-y-2">
                  {picks.map((pid, idx) => {
                    const p = playerMap[pid];
                    return (
                      <div
                        key={pid}
                        data-testid={`selected-pick-${idx}`}
                        className="flex items-center gap-2 p-2 pick-selected bg-black rounded-sm"
                      >
                        <span className="font-display text-lg text-[#00FF66] w-6">{idx + 1}</span>
                        <span className="flex-1 truncate text-sm">
                          {p?.name} <span className="text-white/40">· {p?.team}</span>
                        </span>
                        <button
                          onClick={() => movePick(idx, -1)}
                          disabled={idx === 0}
                          className="p-1 text-white/40 hover:text-white disabled:opacity-20"
                          data-testid={`move-up-${idx}`}
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => movePick(idx, 1)}
                          disabled={idx === picks.length - 1}
                          className="p-1 text-white/40 hover:text-white disabled:opacity-20"
                          data-testid={`move-down-${idx}`}
                        >
                          ▼
                        </button>
                        <button
                          onClick={() => togglePick(pid)}
                          className="p-1 text-white/40 hover:text-[#FF3B30]"
                          data-testid={`remove-pick-${idx}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Tiebreaker */}
            <div
              className="border border-[#FF3B30]/40 p-4 relative overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,59,48,0.08) 0%, rgba(20,20,20,1) 100%)",
              }}
            >
              <div className="flex items-center gap-2 text-[#FF3B30] text-xs uppercase tracking-widest mb-1">
                <Zap size={12} /> Tiebreaker
              </div>
              <div className="font-display text-xl mb-3">{activeTb.label.toUpperCase()}</div>

              <div className="flex flex-wrap gap-1 mb-3">
                {contest.tiebreakers.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTbKey(t.key)}
                    data-testid={`tb-type-${t.key}`}
                    className={`px-3 py-1 text-xs uppercase tracking-widest rounded-sm ${
                      tbKey === t.key ? "bg-[#FF3B30] text-white" : "bg-white/5 text-white/50"
                    }`}
                  >
                    {t.key.replace("_", " ")}
                  </button>
                ))}
              </div>

              <label className="block text-white/40 text-xs uppercase tracking-widest mb-1">
                Which of your picks?
              </label>
              <select
                value={tbPlayer}
                onChange={(e) => setTbPlayer(e.target.value)}
                data-testid="tb-player-select"
                className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF3B30] rounded-sm px-3 py-2 text-sm mb-3 outline-none"
              >
                <option value="">Select a player</option>
                {picks.map((pid) => {
                  const p = playerMap[pid];
                  return (
                    <option key={pid} value={pid}>
                      {`${p?.name || ""} · ${p?.team || ""}`}
                    </option>
                  );
                })}
              </select>

              <label className="block text-white/40 text-xs uppercase tracking-widest mb-1">
                Your prediction
              </label>
              <input
                type="number"
                step="0.1"
                value={tbValue}
                onChange={(e) => setTbValue(e.target.value)}
                data-testid="tb-value-input"
                placeholder="e.g. 3"
                className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#FF3B30] rounded-sm px-3 py-2 text-sm outline-none"
              />
            </div>

            {/* Prize breakdown — practice contests pay nothing */}
            {isPractice ? (
              <div
                className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-5 text-center"
                data-testid="practice-no-prize"
              >
                <div className="text-[#FF9F0A] text-xs uppercase tracking-widest mb-1">Practice contest</div>
                <div className="font-display text-2xl">NO CASH PRIZE</div>
                <div className="text-white/50 text-xs mt-1">
                  Simulated results · nothing is won or lost · no XP awarded
                </div>
              </div>
            ) : (
              <NoPrizePanel />
            )}

            {/* Mini live leaderboard preview */}
            {contest.current_entries > 0 && <LiveLeaderboard contestId={id} />}
          </div>
        </div>
      ) : (
        // Locked or Settled — live board + standings
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {isLocked && <LiveTop5Board data={board} myPicks={myPicks} />}
            <LiveLeaderboard contestId={id} data={board} testId="main-leaderboard" />
          </div>
          <div className="space-y-4">
            {isSettled && <RecapShare contestId={id} />}
            {isPractice ? (
              <div
                className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/5 p-5 text-center"
                data-testid="practice-no-prize"
              >
                <div className="text-[#FF9F0A] text-xs uppercase tracking-widest mb-1">Practice contest</div>
                <div className="font-display text-2xl">NO CASH PRIZE</div>
                <div className="text-white/50 text-xs mt-1">Simulated results · bragging rights only</div>
              </div>
            ) : (
              <NoPrizePanel />
            )}
            {isSettled && contest.actual_top5 && (
              <div className="border border-[#FFD700]/40 bg-[#141414] p-5">
                <div className="text-[#FFD700] text-xs uppercase tracking-widest mb-2">Final Top 5</div>
                <ol className="space-y-2">
                  {contest.actual_top5.map((pid, i) => {
                    const p = playerMap[pid];
                    return (
                      <li key={pid} className="flex items-center gap-3">
                        <span className="w-7 h-7 rounded-sm border border-[#FFD700]/60 text-[#FFD700] flex items-center justify-center font-display">
                          {i + 1}
                        </span>
                        <span className="font-semibold">{p?.name || pid}</span>
                        <span className="text-white/40 text-xs">{p?.team}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
            <div className="border border-white/10 bg-[#141414] p-5">
              <div className="text-white/40 text-xs uppercase tracking-widest mb-2">Contest Info</div>
              <div className="text-white/70 text-sm space-y-1">
                <div>Position: <span className="text-white">{contest.position}</span></div>
                <div>Tier: <span className="text-white">{contest.tier_level.toUpperCase()}</span></div>
                <div>Mode: <span className="text-white">{contest.mode?.toUpperCase() || "REAL"}</span></div>
                <div>Locks: <span className="text-white">{new Date(contest.lock_time).toLocaleString()}</span></div>
                {contest.settle_time && (
                  <div>Settles: <span className="text-white">{new Date(contest.settle_time).toLocaleString()}</span></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {contest.mode === "friends" && friendsInfo?.chat_enabled !== false && (
        <div className={`mt-4 ${isOpen ? "pb-56 md:pb-44" : ""}`} data-testid="friends-chat-section">
          <ChallengeChat
            contestId={id}
            members={friendsInfo?.member_list || []}
            managerId={contest.manager_id}
            me={user}
          />
        </div>
      )}

      {/* Sticky bottom submit slip — only when open */}
      {isOpen && (
        <div className="fixed above-tabbar left-0 right-0 z-30 border-t border-white/10 bg-black/80 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-4 md:gap-6">
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Picks</div>
                <div className="font-display text-2xl">{picks.length}/5</div>
              </div>
              <div>
                <div className="text-white/40 text-xs uppercase tracking-widest">Entry</div>
                <div className="font-display text-2xl text-[#00FF66]">FREE</div>
              </div>
            </div>
            <button
              onClick={submit}
              disabled={submitting || picks.length !== 5 || !tbPlayer || tbValue === ""}
              data-testid="submit-entry-btn"
              className="bg-[#00FF66] hover:bg-[#00e65b] disabled:bg-white/10 disabled:text-white/30 text-black font-bold uppercase tracking-widest px-6 py-3 text-sm rounded-sm inline-flex items-center gap-2"
            >
              {submitting ? "SUBMITTING..." : "Enter Free Challenge"}
              <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />
    </Layout>
  );
}
